package br.com.htms.inspeccaocampo.data.nrvisual

import br.com.htms.inspeccaocampo.data.FotoAnexo
import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.formatDateBr
import br.com.htms.inspeccaocampo.platform.randomUuid

enum class NrAchadoStatus(val rotulo: String) {
    PENDENTE("Pendente de validacao"),
    CONFIRMADO("Confirmado"),
    DESCARTADO("Descartado"),
    NAO_APLICAVEL("Nao aplicavel");

    companion object {
        fun fromName(raw: String?): NrAchadoStatus? =
            raw?.takeIf { it.isNotBlank() }?.let { runCatching { valueOf(it) }.getOrNull() }
    }
}

/** Parecer visual do item em relacao ao objeto detectado. */
enum class NrParecer(val rotulo: String) {
    POSSIVEL_NC("Possivel nao conformidade"),
    CONFORME("Conforme / sem NC evidente"),
    INCONCLUSIVO("Inconclusivo");

    companion object {
        fun fromName(raw: String?): NrParecer =
            raw?.takeIf { it.isNotBlank() }
                ?.let { runCatching { valueOf(it.uppercase()) }.getOrNull() }
                ?: POSSIVEL_NC
    }
}

/**
 * Resultado completo de POST /nr-visual/analyze (objeto-primeiro).
 */
data class NrVisualAnalyzeResult(
    val findings: List<NrAchado> = emptyList(),
    val objetosDetectados: List<String> = emptyList(),
    val cenaSugerida: String = "",
    val resumoCena: String = "",
    val modo: String = "",
    val mensagem: String = ""
)

/**
 * Achado sugerido pela IA / catalogo.
 * So CONFIRMADO + POSSIVEL_NC (ou legado) entra na secao oficial de NC.
 */
data class NrAchado(
    val id: String = randomUuid(),
    val catalogId: String,
    val titulo: String,
    val nr: String,
    val citar: String,
    val gravidade: NrGravidade,
    val oQueViu: String = "",
    val acaoSugerida: String = "",
    val confianca: Double = 0.5,
    val fotoId: String? = null,
    val status: NrAchadoStatus = NrAchadoStatus.PENDENTE,
    val notaTst: String = "",
    val parecer: NrParecer = NrParecer.POSSIVEL_NC,
    val objetoDetectado: String = ""
) {
    val entraNoRelatorioOficial: Boolean
        get() = status == NrAchadoStatus.CONFIRMADO && parecer != NrParecer.CONFORME

    val entraComoObservadoOk: Boolean
        get() = status == NrAchadoStatus.CONFIRMADO && parecer == NrParecer.CONFORME
}

data class DescricaoFotoNr(
    val fotoId: String,
    val numero: Int,
    val texto: String,
    val objetos: List<String> = emptyList()
)

data class PerguntaVisualNr(
    val id: String = randomUuid(),
    val fotoId: String,
    val fotoNumero: Int,
    val objeto: String,
    val texto: String,
    val resposta: String = ""
)

object PerguntasNrVisual {
    data class Modelo(
        val texto: String,
        val catalogId: String,
        val titulo: String
    )

    fun para(objeto: String): List<Modelo> = when (objeto) {
        "extintor" -> listOf(
            Modelo("O acesso ao extintor esta livre?", "NR23_extintor_obstruido", "Extintor com acesso possivelmente bloqueado"),
            Modelo("A sinalizacao do extintor esta visivel?", "NR26_placa_ausente", "Sinalizacao do extintor nao visivel"),
            Modelo("Da para ver lacre ou manometro?", "NR23_extintor_ausente", "Condicao do extintor nao comprovada na foto")
        )
        "hidrante" -> listOf(
            Modelo("O acesso ao hidrante esta livre?", "GERAL_hidrante_obstruido", "Hidrante possivelmente obstruido")
        )
        "escada" -> listOf(
            Modelo("A escada parece apoiada e em condicao de uso?", "NR18_escada_inadequada", "Escada possivelmente inadequada")
        )
        "andaime" -> listOf(
            Modelo("O andaime tem guarda-corpo e piso?", "NR18_andaime_irregular", "Andaime possivelmente irregular")
        )
        "painel", "quadro_eletrico" -> listOf(
            Modelo("O painel esta fechado e sinalizado?", "NR10_painel_aberto", "Painel possivelmente aberto ou sem sinalizacao")
        )
        "cabo" -> listOf(
            Modelo("O cabo esta isolado e fora da passagem?", "NR10_cabos_expostos", "Cabo possivelmente exposto")
        )
        "placa" -> listOf(
            Modelo("A placa esta legivel?", "NR26_placa_ilegivel", "Placa possivelmente ilegivel")
        )
        "maquina" -> listOf(
            Modelo("A protecao da maquina esta no lugar?", "NR12_protecao_ausente", "Protecao de maquina possivelmente ausente")
        )
        "pessoa", "trabalhador", "capacete" -> listOf(
            Modelo("O EPI visivel parece adequado a atividade?", "NR06_epi_danificado", "EPI a conferir na atividade")
        )
        else -> emptyList()
    }
}

data class FormAnaliseVisualNr(
    val tstResponsavel: String = "",
    val obra: String = "",
    val localTexto: String = "",
    val descricaoCena: String = "",
    val cenaTipo: NrCenaTipo = NrCenaTipo.OBRA,
    val fotos: List<FotoAnexo> = emptyList(),
    val achados: List<NrAchado> = emptyList(),
    val analisadoEm: Long? = null,
    val modoAnalise: String = "",
    val normaVersao: String = NrVisualCatalog.NORMA_VERSAO,
    val normaVersaoData: String = NrVisualCatalog.NORMA_VERSAO_DATA,
    val observacoesTst: String = "",
    val dataHorarioMs: Long = currentTimeMillis(),
    val latitude: Double? = null,
    val longitude: Double? = null,
    val objetosDetectados: List<String> = emptyList(),
    val resumoCena: String = "",
    val descricoesFotos: List<DescricaoFotoNr> = emptyList(),
    val perguntas: List<PerguntaVisualNr> = emptyList()
) {
    fun dataHorarioFormatado(): String = formatDateBr(dataHorarioMs)

    fun linkGoogleMaps(): String? {
        val lat = latitude ?: return null
        val lng = longitude ?: return null
        return "https://maps.google.com/?q=$lat,$lng"
    }

    fun achadosConfirmados(): List<NrAchado> = achados.filter { it.entraNoRelatorioOficial }

    fun achadosObservadosOk(): List<NrAchado> = achados.filter { it.entraComoObservadoOk }

    fun achadosPendentes(): List<NrAchado> = achados.filter { it.status == NrAchadoStatus.PENDENTE }

    fun semConteudoBase64(): FormAnaliseVisualNr =
        copy(fotos = fotos.map { it.copy(conteudoBase64 = null) })
}

object AnaliseVisualNrValidacao {
    fun validar(form: FormAnaliseVisualNr): List<String> = buildList {
        if (form.tstResponsavel.isBlank()) add("Informe o TST / responsavel.")
        if (form.obra.isBlank()) add("Selecione a obra.")
        if (form.fotos.isEmpty()) add("Tire pelo menos 1 foto da cena.")
        if (form.fotos.size > 8) add("No maximo 8 fotos por analise.")
        val analisou =
            form.analisadoEm != null ||
                form.achados.isNotEmpty() ||
                form.objetosDetectados.isNotEmpty() ||
                form.modoAnalise.isNotBlank()
        if (!analisou) add("Execute a analise (objeto/IA) antes de salvar.")
        if (form.achados.any { it.status == NrAchadoStatus.PENDENTE && it.parecer != NrParecer.CONFORME }) {
            add("Valide todos os achados (confirmar, descartar ou N/A). Pendentes nao podem permanecer.")
        }
    }
}

/**
 * Identifica os itens mais proximos do catalogo (modo local / fallback sem IA).
 * Nao despeja o catalogo inteiro: exige descricao e devolve no maximo [MAX_CANDIDATOS].
 */
object NrVisualAnaliseLocal {
    const val MAX_CANDIDATOS = 5
    const val MIN_SCORE = 0.18
    const val MSG_SEM_OBJETO =
        "Nao foi possivel identificar pela foto — verifique Workers AI / rede"
    const val MSG_FALHA_VISAO =
        "Nao foi possivel identificar pela foto — verifique Workers AI / rede"

    private val cenaKeywords: List<Pair<NrCenaTipo, List<String>>> = listOf(
        NrCenaTipo.INCENDIO to listOf(
            "extintor", "incendio", "incêndio", "hidrante", "mangueira", "saida de emergencia",
            "saída de emergência", "alarme", "co2", "pqs", "combate a incendio", "rota de fuga"
        ),
        NrCenaTipo.ELETRICA to listOf(
            "painel", "eletrico", "elétrico", "eletrica", "elétrica", "quadro", "lototo",
            "nr-10", "nr10", "disjuntor", "cabo energizado", "aterramento", "extensao improvisada"
        ),
        NrCenaTipo.ALTURA to listOf(
            "cinto", "talabarte", "andaime", "plataforma", "linha de vida", "nr-35", "nr35",
            "guarda-corpo", "guarda corpo", "trabalho em altura"
        ),
        NrCenaTipo.OFICINA to listOf(
            "maquina", "máquina", "torno", "prensa", "emergencia", "emergência", "protecao de parte movel",
            "nr-12", "nr12", "serra", "lixadeira"
        ),
        NrCenaTipo.CIRCULACAO to listOf(
            "circulacao", "circulação", "passagem", "corredor", "almoxarifado", "piso", "placa",
            "sinalizacao", "sinalização", "evacuacao", "evacuação"
        ),
        NrCenaTipo.OBRA to listOf(
            "obra", "capacete", "botas", "luva", "andaime", "vala", "escavacao", "escavação",
            "tapume", "calcada", "calçada", "ferramenta", "epi"
        )
    )

    fun detectarCena(descricao: String, fallback: NrCenaTipo = NrCenaTipo.GERAL): NrCenaTipo {
        val texto = descricao.lowercase()
        if (texto.isBlank()) return fallback
        var melhor = fallback
        var melhorScore = 0
        for ((cena, keys) in cenaKeywords) {
            val hits = keys.count { it in texto }
            if (hits > melhorScore) {
                melhorScore = hits
                melhor = cena
            }
        }
        return melhor
    }

    private val stopwords = setOf(
        "sem", "com", "para", "pela", "pelo", "uma", "uns", "das", "dos", "que", "nao",
        "ausente", "ausentes", "geral", "item", "foto", "cena"
    )

    /** Sinonimos / pistas da descricao -> tokens do catalogo. */
    private val sinonimos: List<Pair<String, List<String>>> = listOf(
        "buraco" to listOf("abertura", "piso", "buraco"),
        "laje" to listOf("abertura", "piso", "laje"),
        "tampa" to listOf("abertura", "piso"),
        "beiral" to listOf("borda", "altura", "cinto"),
        "telhado" to listOf("borda", "altura", "cinto"),
        "rodape" to listOf("andaime", "queda", "material"),
        "ferramentas" to listOf("queda", "material", "ferramenta"),
        "borda" to listOf("borda", "queda", "material"),
        "extensoe" to listOf("extensao", "cabo", "exposto"),
        "extensao" to listOf("extensao", "cabo"),
        "desencapad" to listOf("cabo", "exposto", "extensao"),
        "cabo" to listOf("cabo", "exposto"),
        "arame" to listOf("bypass", "dispositivo", "protecao"),
        "amarrad" to listOf("bypass", "dispositivo"),
        "burlad" to listOf("bypass"),
        "bypass" to listOf("bypass"),
        "corredor" to listOf("circulacao", "passagem", "corredor", "obstru"),
        "palete" to listOf("circulacao", "empilhamento", "obstru"),
        "empilh" to listOf("empilhamento"),
        "cintamento" to listOf("empilhamento"),
        "saida" to listOf("rota", "saida", "fuga", "obstru"),
        "emergencia" to listOf("rota", "saida", "emergencia"),
        "carrinho" to listOf("rota", "obstru", "bloque"),
        "vala" to listOf("escavacao", "poco", "tapume", "risco", "isolamento"),
        "fita" to listOf("isolamento", "risco", "tapume"),
        "zebrad" to listOf("isolamento", "risco"),
        "isolamento" to listOf("isolamento", "risco"),
        "escav" to listOf("escavacao"),
        "tapume" to listOf("tapume", "escavacao"),
        "calcada" to listOf("tapume", "escavacao"),
        "loto" to listOf("bloqueio", "loto", "painel"),
        "cadeado" to listOf("bloqueio", "loto"),
        "etiqueta" to listOf("bloqueio", "loto"),
        "ccm" to listOf("painel", "bloqueio", "eletr"),
        "seccionador" to listOf("bloqueio", "painel"),
        "trava-quedas" to listOf("cinto", "trava"),
        "trava quedas" to listOf("cinto", "trava"),
        "gaiola" to listOf("escada", "marinheiro", "gaiola"),
        "extintor" to listOf("extintor"),
        "hidrante" to listOf("hidrante"),
        "painel" to listOf("painel"),
        "circulacao" to listOf("circulacao", "passagem")
    )

    fun scoreItem(item: NrCatalogItem, texto: String): Double {
        // HARD_EXTINTOR_NO_NR06
        if (("extintor" in texto || "incendio" in texto || "hidrante" in texto) &&
            item.id.startsWith("NR06_")
        ) {
            return 0.0
        }
        if (texto.isBlank()) return 0.0
        var score = 0.0
        val skip = setOf("nr06", "nr10", "nr12", "nr18", "nr23", "nr26", "nr35", "geral")
        val hay = (
            item.id + " " + item.titulo + " " + item.oQueProcurar + " " + item.secao + " " + item.nr
        ).lowercase()

        val idTokens = item.id.lowercase().split('_').filter { it.length > 2 && it !in skip && it !in stopwords }
        for (tok in idTokens) {
            if (tok in texto) score += 0.24
        }
        for (w in item.titulo.lowercase().split(' ', '-', '/')) {
            if (w.length > 3 && w !in stopwords && w in texto) score += 0.18
        }
        for (w in item.oQueProcurar.lowercase().split(' ', '-', '/', ',')) {
            if (w.length > 3 && w !in stopwords && w in texto) score += 0.14
        }
        for ((pista, tokens) in sinonimos) {
            if (pista in texto) {
                for (tok in tokens) {
                    if (tok in hay) score += 0.2
                }
            }
        }
        if (("extintor" in texto || "incendio" in texto || "hidrante" in texto) &&
            item.id.startsWith("NR23_")
        ) {
            score += 0.25
        }
        if (("painel" in texto || "eletr" in texto || "ccm" in texto) && item.id.startsWith("NR10_")) {
            score += 0.18
        }
        if (("corredor" in texto || "passagem" in texto || "circulacao" in texto) &&
            ("circulacao" in item.id.lowercase() || "obstru" in item.id.lowercase() || "passagem" in hay)
        ) {
            score += 0.2
        }
        return score.coerceAtMost(1.0)
    }

    fun gerarCandidatos(
        cena: NrCenaTipo,
        descricaoCena: String = "",
        fotoIds: List<String> = emptyList()
    ): List<NrAchado> {
        val textoBruto = descricaoCena.trim()
        if (textoBruto.isBlank()) return emptyList()
        val texto = textoBruto.lowercase()
        val objetos = NrVisualObjectMap.detectarObjetosNaDescricao(textoBruto)
        val detectada = NrVisualObjectMap.cenaSugeridaParaObjetos(objetos)
            ?: detectarCena(textoBruto, cena)
        val itens = (
            NrVisualCatalog.itensParaCena(cena) +
                if (detectada != cena) NrVisualCatalog.itensParaCena(detectada) else emptyList()
            ).distinctBy { it.id }
        val ranqueados = itens.mapNotNull { item ->
            val score = scoreItem(item, texto)
            if (score < MIN_SCORE) null else item to score
        }.sortedByDescending { it.second }
            .filter { (item, _) ->
                objetos.isEmpty() || NrVisualObjectMap.catalogIdPermitido(item.id, objetos)
            }
            .take(MAX_CANDIDATOS)

        val achados = ranqueados.mapIndexed { index, (item, score) ->
            NrAchado(
                catalogId = item.id,
                titulo = item.titulo,
                nr = item.nr,
                citar = item.citar,
                gravidade = item.gravidadePadrao,
                oQueViu = "Candidato mais proximo do catalogo visual (${item.oQueProcurar}). Validar na foto.",
                acaoSugerida = item.acao,
                confianca = (0.35 + score * 0.45).coerceIn(0.35, 0.85),
                fotoId = fotoIds.getOrNull(index % fotoIds.size.coerceAtLeast(1)),
                status = NrAchadoStatus.PENDENTE,
                parecer = NrParecer.POSSIVEL_NC,
                objetoDetectado = objetos.firstOrNull().orEmpty()
            )
        }
        return NrVisualObjectMap.reforcarRegrasDuras(
            NrVisualObjectMap.filtrarAchados(achados, objetos),
            objetos
        )
    }

}
